import {
  Box,
  Chip,
  InputAdornment,
  makeStyles,
  TextField,
  withStyles,
} from '@material-ui/core';
import { useCallback, useMemo, useTransition } from 'react';

import FilterListIcon from '@material-ui/icons/FilterList';
import SearchIcon from '@material-ui/icons/Search';
import ToggleMenu from './ToggleMenu';
import useTranslation from 'next-translate/useTranslation';
import Hidden from './HiddenSSRCompatible';
import { Filter } from 'lucide-react';

const StyledTextField = withStyles({
  root: {
    width: '100%',
    '& .MuiInput-root': {
      color: 'inherit',
      '& .MuiInputAdornment-root': {
        color: 'inherit',
      },
    },
  },
})(TextField);

const useStyles = makeStyles((theme) => ({
  inprogress: {
    color: theme.palette.info.contrastText,
    backgroundColor: "#43A047",
  },
  occupied: {
    color: theme.palette.info.contrastText,
    backgroundColor: "#43A047",
  },
  booked: {
    color: theme.palette.info.contrastText,
    backgroundColor: "#43A09E"
  },
  end_soon: {
    color: theme.palette.info.contrastText,
    backgroundColor: "#A04392"
  },
  available_soon: {
    color: theme.palette.info.contrastText,
    backgroundColor: "#A04392"
  },
  vacant: {
    color: theme.palette.info.contrastText,
    backgroundColor: "#64B5F6"
  },
  "-": {
    color: theme.palette.info.contrastText,
    backgroundColor: "#64B5F6"
  }
}))

export default function SearchFilterBar({
  searchText = '',
  selectedIds = [],
  statusList = [],
  onSearch,
  label,
  maxWidth,
  filterIcon = <Filter />,
  isShowChipFilter = true
}) {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const [, startTransition] = useTransition();
  const currentSelectedStatus = useMemo(() => {
    return selectedIds.map((value) =>
      statusList.find(({ id }) => id === value)
    );
  }, [selectedIds, statusList]);

  const triggerSearch = useCallback(
    (inputSelectedStatus, inputSearchText) => {
      startTransition(() => {
        onSearch(inputSelectedStatus, inputSearchText);
      });
    },
    [onSearch]
  );

  const handleTextChange = useCallback(
    (event) => {
      triggerSearch(currentSelectedStatus, event.target.value || '');
    },
    [currentSelectedStatus, triggerSearch]
  );

  const handleToggleChange = useCallback(
    (values) => {
      triggerSearch(values, searchText);
    },
    [searchText, triggerSearch]
  );

  const handleDeleteFilter = useCallback(
    (item) => () => {
      const values = currentSelectedStatus.filter(({ id }) => item.id !== id);
      triggerSearch(values, searchText);
    },
    [currentSelectedStatus, searchText, triggerSearch]
  );

  return (
    <>
      <Hidden smDown>
        <Box mb={1}>
          <Box display="flex" flexWrap="nowrap" alignItems="center" width={maxWidth ? maxWidth : 330}>
            <Box flexGrow={1} className='shepherd-searchBar'>
              <StyledTextField
                placeholder={`${t('Search')} ${label ? label : ""}`}
                defaultValue={searchText}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={handleTextChange}
              />
            </Box>
            <ToggleMenu
              className='shepherd-toggleStatusMenu'
              startIcon={filterIcon}
              options={statusList}
              selectedIds={selectedIds}
              onChange={handleToggleChange}
              multi
            />
          </Box>
          {isShowChipFilter && (
            <Box display="flex" gridGap={10} py={0.5} className='shepherd-statusValue'>
              {selectedIds
                .filter((id) => id !== '')
                .map((id) => statusList.find((status) => status?.id === id))
                .map((status) => (
                  <Chip
                    key={status.id}
                    label={status.label}
                    size="small"
                    onDelete={handleDeleteFilter(status)}
                    className={classes[status.id]}
                  />
                ))}
            </Box>
          )}
        </Box>
      </Hidden>
      <Hidden mdUp>
        <Box mt={2} mb={2}>
          <Box display="flex" flexWrap="nowrap" alignItems="center" width={'100%'}>
            <Box flexGrow={1} className='shepherd-searchBar'>
              <StyledTextField
                placeholder={`${t('Search')} ${label ? label : ""}`}
                defaultValue={searchText}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={handleTextChange}
              />
            </Box>
            <ToggleMenu
              className='shepherd-toggleStatusMenu'
              startIcon={filterIcon}
              options={statusList}
              selectedIds={selectedIds}
              onChange={handleToggleChange}
              multi
            />
          </Box>
          {isShowChipFilter && (
            <Box display="flex" gridGap={10} py={0.5} className='shepherd-statusValue'>
              {selectedIds
                .filter((id) => id !== '')
                .map((id) => statusList.find((status) => status?.id === id))
                .map((status) => (
                  <Chip
                    key={status.id}
                    label={status.label}
                    size="small"
                    onDelete={handleDeleteFilter(status)}
                    className={classes[status.id]}
                  />
                ))}
            </Box>
          )}
        </Box>
      </Hidden>
    </>
  );
}
